import type {
  AccountListCreateResult,
  AccountListDetail,
  AccountListSummary,
  ImportPreview,
  ImportPreviewItem,
  LedgerItem,
  ProxyItem,
  ProxyImportResult,
  SubConnection,
  SubBrokenAccountScan,
  SubConnectionInput,
  SubOperationLog,
  TaskActionResult,
  TaskCreateInput,
  TaskCreateResult,
  TaskDetail,
  TaskExport,
  TaskSummary,
  User,
} from "./types";
import { previewRead } from "./preview";

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");
export const isPreviewMode = import.meta.env.VITE_PREVIEW_MODE === "true";
export const AUTH_EXPIRED_EVENT = "oauth:auth-expired";

export class ApiError extends Error {
  status: number;
  details: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

function csrfToken() {
  const pairs = document.cookie.split("; ");
  const entry =
    pairs.find((item) => item.startsWith("oauth_new_csrf=")) ||
    pairs.find((item) => item.startsWith("oauth_csrf="));
  if (!entry) return "";
  return decodeURIComponent(entry.slice(entry.indexOf("=") + 1));
}

function errorMessage(payload: unknown, fallback: string) {
  if (!payload || typeof payload !== "object") return fallback;
  const data = payload as Record<string, unknown>;
  const detail = data.detail ?? data.message;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (!item || typeof item !== "object") return String(item);
        const record = item as Record<string, unknown>;
        return typeof record.msg === "string" ? record.msg : JSON.stringify(record);
      })
      .join("；");
  }
  if (detail && typeof detail === "object") {
    const nested = detail as Record<string, unknown>;
    if (typeof nested.message === "string") return nested.message;
    return JSON.stringify(detail);
  }
  return fallback;
}

function notifyAuthExpired(path: string, status: number) {
  if (status !== 401 || path.startsWith("/api/auth/")) return;
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

function requestHeaders(init: RequestInit) {
  const method = (init.method || "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (method !== "GET" && method !== "HEAD") headers.set("X-CSRF-Token", csrfToken());
  return { method, headers };
}

async function parsePayload(response: Response): Promise<unknown> {
  const raw = await response.text();
  try {
    return raw ? JSON.parse(raw) : {};
  } catch {
    return { message: raw };
  }
}

async function requestResponse(path: string, init: RequestInit = {}) {
  if (isPreviewMode) throw new ApiError("只读预览不会执行写入或下载操作", 405);
  const { method, headers } = requestHeaders(init);
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    method,
    headers,
    credentials: "include",
  });
  if (!response.ok) {
    const payload = await parsePayload(response);
    notifyAuthExpired(path, response.status);
    throw new ApiError(errorMessage(payload, `请求失败（${response.status}）`), response.status, payload);
  }
  return response;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method || "GET").toUpperCase();
  if (isPreviewMode) {
    if (method !== "GET") throw new ApiError("只读预览不会执行写入操作", 405);
    const value = previewRead(path);
    if (value === undefined) throw new ApiError("预览数据不包含此接口", 404);
    await new Promise((resolve) => window.setTimeout(resolve, 120));
    return structuredClone(value) as T;
  }
  const response = await requestResponse(path, init);
  const payload = await parsePayload(response);
  if (!response.ok) {
    notifyAuthExpired(path, response.status);
    throw new ApiError(errorMessage(payload, `请求失败（${response.status}）`), response.status, payload);
  }
  return payload as T;
}

export function isEndpointUnavailable(error: unknown) {
  return error instanceof ApiError && (error.status === 404 || error.status === 405 || error.status === 501);
}

export function createIdempotencyKey(scope: string) {
  const id = typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${scope}:${id}`;
}

function idempotentHeaders(key: string) {
  return { "Idempotency-Key": key };
}

function normalizePreviewItem(item: Partial<ImportPreviewItem>): ImportPreviewItem {
  return {
    line_no: Number(item.line_no || 0),
    ok: Boolean(item.ok),
    email: item.email || "",
    login_identity: item.login_identity || "",
    mailapi_url: item.mailapi_url || "",
    errors: Array.isArray(item.errors) ? item.errors.map(String) : [],
    warnings: Array.isArray(item.warnings) ? item.warnings.map(String) : [],
  };
}

function filenameFromResponse(response: Response, fallback: string) {
  const disposition = response.headers.get("Content-Disposition") || "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try { return decodeURIComponent(encoded); } catch { return fallback; }
  }
  return disposition.match(/filename="?([^";]+)"?/i)?.[1] || fallback;
}

export const api = {
  auth: {
    me: () => request<User>("/api/auth/me"),
    login: (username: string, password: string) =>
      request<User>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      }),
    register: (username: string, password: string) =>
      request<User>("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      }),
    logout: () => request<Record<string, unknown>>("/api/auth/logout", { method: "POST" }),
  },
  tasks: {
    list: () => request<{ items: TaskSummary[] }>("/api/tasks"),
    detail: (id: number) => request<TaskDetail>(`/api/tasks/${id}`),
    create: (input: TaskCreateInput, key: string) =>
      request<TaskCreateResult>("/api/v2/tasks", {
        method: "POST",
        headers: idempotentHeaders(key),
        body: JSON.stringify(input),
      }),
    cancel: async (id: number, key: string) => {
      try {
        return await request<TaskActionResult>(`/api/v2/tasks/${id}/cancel`, {
          method: "POST",
          headers: idempotentHeaders(key),
        });
      } catch (error) {
        if (!isEndpointUnavailable(error)) throw error;
        return request<TaskActionResult>(`/api/tasks/${id}/cancel`, {
          method: "POST",
          headers: idempotentHeaders(key),
        });
      }
    },
    retry: (id: number, itemIds: number[], key: string) =>
      request<TaskCreateResult & { source_task_id?: number }>(`/api/v2/tasks/${id}/retry`, {
        method: "POST",
        headers: idempotentHeaders(key),
        body: JSON.stringify(itemIds.length ? { item_ids: itemIds } : {}),
      }),
    exportResult: async (id: number): Promise<TaskExport> => {
      try {
        const response = await requestResponse(`/api/v2/tasks/${id}/export`);
        return {
          blob: await response.blob(),
          filename: filenameFromResponse(response, `task_${id}_success.jsonl`),
        };
      } catch (error) {
        if (!isEndpointUnavailable(error)) throw error;
        const legacy = await request<{ items: unknown[] }>(`/api/tasks/${id}/success`);
        const body = (legacy.items || []).map((item) => JSON.stringify(item)).join("\n");
        return { blob: new Blob([body], { type: "application/x-ndjson;charset=utf-8" }), filename: `task_${id}_success.jsonl` };
      }
    },
  },
  accountLists: {
    list: () => request<{ items: AccountListSummary[] }>("/api/account-lists"),
    detail: (id: number) => request<AccountListDetail>(`/api/account-lists/${id}`),
    preflight: async (jsonl: string, key: string): Promise<ImportPreview> => {
      try {
        const result = await request<Omit<ImportPreview, "transport">>("/api/v2/imports/preflight", {
          method: "POST",
          headers: idempotentHeaders(key),
          body: JSON.stringify({ jsonl }),
        });
        return { ...result, items: (result.items || []).map(normalizePreviewItem), transport: "v2" };
      } catch (error) {
        if (!isEndpointUnavailable(error)) throw error;
        const result = await request<{
          summary: ImportPreview["summary"];
          items: ImportPreviewItem[];
        }>("/api/tasks/validate", {
          method: "POST",
          headers: idempotentHeaders(key),
          body: JSON.stringify({ jsonl, probe_mailapi: false, max_probe_lines: 0 }),
        });
        return {
          preview_id: "legacy",
          expires_at: null,
          summary: result.summary,
          items: (result.items || []).map(normalizePreviewItem),
          transport: "legacy",
        };
      }
    },
    createFromPreview: async (preview: ImportPreview, name: string, jsonl: string, key: string) => {
      if (preview.transport === "v2") {
        return request<AccountListCreateResult>(`/api/v2/imports/${encodeURIComponent(preview.preview_id)}/account-list`, {
          method: "POST",
          headers: idempotentHeaders(key),
          body: JSON.stringify({ name }),
        });
      }
      return request<AccountListCreateResult>("/api/account-lists", {
        method: "POST",
        headers: idempotentHeaders(key),
        body: JSON.stringify({ name, jsonl }),
      });
    },
  },
  integrations: {
    connections: () => request<{ items: SubConnection[] }>("/api/sub2/connections"),
    createConnection: (input: SubConnectionInput, key: string) => request<{ connection: SubConnection }>("/api/sub2/connections", { method: "POST", headers: idempotentHeaders(key), body: JSON.stringify(input) }),
    updateConnection: (id: number, input: Partial<SubConnectionInput>, key: string) => request<{ connection: SubConnection }>(`/api/sub2/connections/${id}`, { method: "PATCH", headers: idempotentHeaders(key), body: JSON.stringify(input) }),
    testConnection: (id: number, key: string) => request<Record<string, unknown>>(`/api/sub2/connections/${id}/test`, { method: "POST", headers: idempotentHeaders(key) }),
    deleteConnection: (id: number, key: string) => request<{ status: string; connection_id: number }>(`/api/sub2/connections/${id}`, { method: "DELETE", headers: idempotentHeaders(key) }),
    brokenAccounts: (connectionId: number) => request<SubBrokenAccountScan>(`/api/sub2/connections/${connectionId}/broken-accounts`),
    proxies: () => request<{ items: ProxyItem[]; total: number }>("/api/proxies"),
    importProxies: (proxies: string, replace: boolean, key: string) => request<ProxyImportResult>("/api/proxies/import", { method: "POST", headers: idempotentHeaders(key), body: JSON.stringify({ proxies, replace }) }),
    deleteProxies: (proxyIds: number[], key: string) => request<{ status: string; deleted: number; requested: number }>("/api/proxies/batch-delete", { method: "POST", headers: idempotentHeaders(key), body: JSON.stringify({ proxy_ids: proxyIds }) }),
    logs: () => request<{ items: SubOperationLog[] }>("/api/sub2/operation-logs"),
  },
  credits: {
    balance: () => request<{ balance: number }>("/api/credits/balance"),
    ledger: () => request<{ items: LedgerItem[] }>("/api/credits/ledger"),
    redeem: (code: string) =>
      request<{ status: string; points: number; balance: number }>("/api/credits/redeem", {
        method: "POST",
        body: JSON.stringify({ code }),
      }),
  },
};
